<script setup>
    import Layout from "@/layouts/main.vue";
    import PageHeader from "@/components/page-header";
    import useVuelidate from "@vuelidate/core";
    import { required, helpers } from "@vuelidate/validators";
    import { computed, onMounted, ref, reactive } from 'vue';
    import { useStore } from 'vuex';
    import Multiselect from '@suadelabs/vue3-multiselect';
    import "@suadelabs/vue3-multiselect/dist/vue3-multiselect.css";
    import { announcementCategories } from "@/helpers/constants";

    const state = reactive({
        title: '',
        description: '',
        category: 'organization',
        cameras: [],
        starts_at: '',
        ends_at: '',
    });

    const isCameraCategory = computed(() => state.category === 'camera');

    const rules = computed(() => ({
        title: {
            required: helpers.withMessage("Announcement title is required", required)
        },
        description: {
            required: helpers.withMessage("Announcement description is required", required),
        },
        category: {
            required: helpers.withMessage("Announcement category is required", required),
        },
        cameras: {
            requiredForCamera: helpers.withMessage(
                "Select at least one camera for a camera announcement",
                (value) => !isCameraCategory.value || (value && value.length > 0)
            ),
        },
        ends_at: {
            afterStart: helpers.withMessage(
                "End date must be after the start date",
                (value) => !value || !state.starts_at || new Date(value) > new Date(state.starts_at)
            ),
        },
    }));

    const store = useStore();
    const v$ = useVuelidate(rules, state);
    const loading = computed(() => store.state.globals.loading);
    const cameraOptions = computed(() => store.state.announcement.camera_options);

    const title = ref("Announcement");
    const items = ref([
        {
            text: "Home",
            href: "/",
        },
        {
            text: "Announcement",
            href: "/announcement/list",
            active: true
        },
        {
            text: "Add New Announcement",
            active: true,
        }
    ]);

    const submitted = ref(false);
    const notification = computed(() => store.state.notification);

    const cameraLabel = (camera) => [
        camera.lgu ? `[${camera.lgu.abbreviation || camera.lgu.name}]` : null,
        camera.camera_id,
        camera.location,
    ].filter(Boolean).join(' - ');

    const onCategoryChange = () => {
        if (!isCameraCategory.value) state.cameras = [];
    }

    const createAnnouncement = () => {
        submitted.value = true
        v$.value.$touch();

        if (v$.value.$invalid) return;

        store.dispatch('announcement/createAnnouncement', {
            title: state.title,
            description: state.description,
            category: state.category,
            camera_ids: isCameraCategory.value ? state.cameras.map((camera) => camera.id) : [],
            starts_at: state.starts_at || null,
            ends_at: state.ends_at || null,
        });
    }

    onMounted(() => {
        store.dispatch('announcement/getCameraOptions');
    });
</script>

<template>
  <Layout>
    <PageHeader back="/announcement/list" :title="title" :items="items" />
    <div class="row">
        <div class="col lg-12">
            <b-overlay :show="loading">
                <div class="card">
                    <div class="card-header">
                        <h4 class="card-title mb-0">Add New Announcement</h4>
                    </div>
                    <div class="card-body">
                        <form @submit.prevent="createAnnouncement" class="mx-2">
                            <div v-if="notification.messages.length > 0">
                                <b-alert v-for="(message, index) in notification.messages" :key="index"
                                    show variant="danger" class="fade show" role="alert">
                                    {{ message }}
                                </b-alert>
                            </div>
                            <div class="row">
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="title" class="form-label required">Announcement Title</label>
                                        <input
                                            autofocus
                                            :class="{ 'is-invalid': submitted && v$.title.$error }"
                                            v-model="state.title" type="text" class="form-control" placeholder="Enter announcement title..." id="title" />
                                        <div
                                            v-for="item of v$.title.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="description" class="form-label required">Announcement Description</label>
                                        <textarea
                                            :class="{ 'is-invalid': submitted && v$.description.$error }"
                                            v-model="state.description" class="form-control" placeholder="Enter announcement description..." id="description"></textarea>
                                        <div
                                            v-for="item of v$.description.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="category" class="form-label required">Category</label>
                                        <select
                                            id="category"
                                            class="form-select"
                                            :class="{ 'is-invalid': submitted && v$.category.$error }"
                                            v-model="state.category"
                                            @change="onCategoryChange"
                                        >
                                            <option v-for="option in announcementCategories" :key="option.value" :value="option.value">
                                                {{ option.name }}
                                            </option>
                                        </select>
                                        <div class="form-text">
                                            Organization announcements reach everyone. Camera announcements also show
                                            up in the Mismatch and Review queues for records from the selected cameras.
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6" v-if="isCameraCategory">
                                    <div class="mb-3">
                                        <label for="cameras" class="form-label required">Cameras</label>
                                        <multiselect
                                            id="cameras"
                                            :class="{ 'is-invalid': submitted && v$.cameras.$error }"
                                            :multiple="true"
                                            v-model="state.cameras"
                                            :close-on-select="false"
                                            :options="cameraOptions"
                                            :custom-label="cameraLabel"
                                            placeholder="Select camera(s)"
                                            track-by="id"
                                        />
                                        <div
                                            v-for="item of v$.cameras.$errors"
                                            :key="item.$uid"
                                            class="text-danger small mt-1"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="starts_at" class="form-label">Start Date &amp; Time</label>
                                        <input v-model="state.starts_at" type="datetime-local" class="form-control" id="starts_at" />
                                        <div class="form-text">Leave blank to publish immediately.</div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="ends_at" class="form-label">End Date &amp; Time</label>
                                        <input
                                            :class="{ 'is-invalid': submitted && v$.ends_at.$error }"
                                            v-model="state.ends_at" type="datetime-local" class="form-control" id="ends_at" />
                                        <div class="form-text">
                                            The announcement is archived automatically once this passes.
                                            Leave blank to keep it running until archived manually.
                                        </div>
                                        <div
                                            v-for="item of v$.ends_at.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-lg-12">
                                    <div class="text-end">
                                        <router-link to="/announcement/list" type="submit" class="btn btn-danger mr-3">
                                            <i class="ri-close-fill fs-4 mr-1 valign-middle"></i>
                                            Cancel
                                        </router-link>
                                        <button type="submit" class="btn btn-success">
                                            <i class="ri-save-2-fill fs-4 mr-1 valign-middle"></i>
                                            Add Announcement
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </b-overlay>
        </div>
    </div>
  </Layout>
</template>
